import { DateTime } from 'luxon'
import * as z from 'zod/mini'

/** `cancellationDeadline` value: the customer may never cancel themselves. */
export const CANCELLATION_NEVER = -1

/** `cancellationDeadline` value: the customer may cancel until the start. */
export const CANCELLATION_UNTIL_START = 0

/**
 * Minutes before the start after which the customer may no longer cancel through their link. `-1`: never; `0`: until
 * the start. At most 30 days. A reservation and an event sign-up copy it when they are made, so a later change to the
 * service or the event does not move the rule under people already booked (docs/reservations.md, docs/events.md).
 */
export const cancellationDeadlineField = z.int().check(z.minimum(-1), z.maximum(43200))

export interface CancellationTerms {
  start: Date | string
  cancellationDeadline: number
}

/** Why the customer may not cancel; `null` from `customerCancellationRefusal` means they may. */
export type CustomerCancellationRefusal = 'NOT_ALLOWED' | 'DEADLINE_PASSED'

/** The last instant the customer may cancel (exclusive), or `null` when they may never. */
export function cancellationDeadlineAt(terms: CancellationTerms): Date | null {
  if (terms.cancellationDeadline === CANCELLATION_NEVER) {
    return null
  }
  return new Date(new Date(terms.start).getTime() - terms.cancellationDeadline * 60_000)
}

/**
 * Whether the customer may cancel through their own link at `now`. The deadline is an instant: `0` puts it at the
 * start, so nobody cancels a reservation or event that is already under way. Staff are not bound by it.
 */
export function customerCancellationRefusal(
  terms: CancellationTerms,
  now: Date = new Date(),
): CustomerCancellationRefusal | null {
  const deadline = cancellationDeadlineAt(terms)
  if (!deadline) {
    return 'NOT_ALLOWED'
  }
  return now < deadline ? null : 'DEADLINE_PASSED'
}

/**
 * The deadline for an e-mail, in the premise's zone, to follow "do": "15. októbra 2026, 9:00". Empty when the customer
 * may never cancel, so the template's `{{#…}}` section hides.
 */
export function formatCancellationDeadlineInZone(terms: CancellationTerms, zone: string, locale = 'sk'): string {
  const deadline = cancellationDeadlineAt(terms)
  if (!deadline) {
    return ''
  }
  return DateTime.fromJSDate(deadline).setZone(zone).setLocale(locale).toFormat('d. MMMM yyyy, H:mm')
}
